/**
 * HeartImmersionRu — Russian-language variant of the Heart Flow
 * healing immersion for Baba Olya. Structurally identical to
 * HeartImmersion (3 silent breaths → 5 min immersion → dissolve),
 * reusing the same HeartVisualEngine so the healing session grandma
 * receives is the ACTUAL heart healing we already have — only the
 * voice track and on-screen text are localised.
 *
 * Voice track: /scripts/samples/grandma_session_full.mp3
 *   · Mia's real recording (0:08–1:37)
 *   · Two ElevenLabs "coming back" moments (~2:02 & ~2:53)
 *   · Signature heart-flow music + theta binaural + subliminal Mia loop
 *
 * Total duration:  300 s  (5 min — matches the master mix)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, SkipForward } from 'lucide-react';
import HeartVisualEngine from '../HealingSupport/HeartVisualEngine';

// The master mix bundles voice + music + binaural + subliminal already,
// so we don't need to layer them again in the browser. Fixed levels below.
const AUDIO_URL = '/scripts/samples/grandma_session_full.mp3';

const TOTAL_DURATION = 300;
const DISSOLVE_START = 280;

const BREATH_INHALE = 4000;
const BREATH_HOLD   = 2000;
const BREATH_EXHALE = 6000;
const BREATH_REST   = 2000;
const TOTAL_BREATHS = 3;

// Russian text-sync phrases — mapped to the arc of the master mix.
// Timings are relative to when the voice track starts (audio play() call)
// which happens ~1.5 s after `mode === 'immersion'` — the offsets below
// are elapsed-seconds within the immersion timer so line up.
const TEXT_SYNC = [
  // 0:00–0:30 · Arrival — soften, come home to the heart
  { t: 4,    text: 'мягко\u2026',                              dur: 3200 },
  { t: 14,   text: 'к центру груди\u2026',                     dur: 3600 },
  { t: 30,   text: 'тёплый свет\u2026',                        dur: 3600 },
  // 0:30–1:30 · Light begins to expand with each breath
  { t: 50,   text: 'с каждым вдохом\u2026',                    dur: 3600 },
  { t: 72,   text: 'позволяй\u2026',                           dur: 3200 },
  { t: 92,   text: 'без усилий\u2026',                         dur: 3600 },
  // 1:30–2:00 · Warmth · comfort · support · safety
  { t: 108,  text: 'тепло',                                    dur: 2400 },
  { t: 114,  text: 'комфорт',                                  dur: 2400 },
  { t: 120,  text: 'поддержка',                                dur: 2400 },
  { t: 126,  text: 'безопасность',                             dur: 3000 },
  // 2:00–2:15 · Come-back moment 1 (Mia returns) → healing energy
  { t: 145,  text: 'позволь исцелению происходить\u2026',      dur: 5000 },
  // 2:15–3:00 · Deepening — being held, safe to soften
  { t: 165,  text: 'тебя держат\u2026',                        dur: 3600 },
  { t: 180,  text: 'здесь безопасно смягчиться\u2026',         dur: 4200 },
  // 3:00–3:20 · Come-back moment 2 (Mia's final return)
  { t: 200,  text: 'просто дыши\u2026',                        dur: 3600 },
  // 3:20–4:20 · Integration — this is yours, light remains
  { t: 220,  text: 'спокойно\u2026',                           dur: 3000 },
  { t: 240,  text: 'ровно\u2026',                              dur: 3000 },
  { t: 260,  text: 'без усилий\u2026',                         dur: 3600 },
  // 4:20–5:00 · Rest and dissolve
  { t: 278,  text: 'отдыхай\u2026',                            dur: 5000 },
];

// 12 phrases the app displays as subliminal cycling text (very low
// opacity) — same as the audio subliminal bed, so grandma is hearing
// and momentarily glimpsing the words at the same time.
const SUBLIMINAL_RU = [
  'мягко', 'сердце', 'свет', 'сияние', 'позволь', 'тепло',
  'комфорт', 'поддержка', 'безопасно', 'тебя держат', 'ровно', 'спокойно',
];

const BREATH_LABELS = {
  inhale: 'вдох',
  hold:   'задержи',
  exhale: 'выдох',
};

const HeartImmersionRu = ({ onComplete }) => {
  const [mode, setMode] = useState('waiting');
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [dissolveOpacity, setDissolveOpacity] = useState(0);

  const [breathCount, setBreathCount] = useState(0);
  const [breathLabel, setBreathLabel] = useState('');
  const [breathScale, setBreathScale] = useState(1);
  const breathTimerRef = useRef(null);

  const [overlayText, setOverlayText] = useState(null);
  const shownTextsRef = useRef(new Set());

  const [subliminalText, setSubliminalText] = useState('');
  const subliminalTimerRef = useRef(null);
  const subliminalIndexRef = useRef(0);

  const elapsedRef = useRef(0);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const formatMins = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Breathing sequence — 3 silent inhale/hold/exhale/rest cycles
  const runBreathCycle = useCallback((n) => {
    if (n >= TOTAL_BREATHS) {
      setMode('immersion');
      return;
    }
    setBreathCount(n);
    setBreathLabel(BREATH_LABELS.inhale);
    setBreathScale(1.6);
    breathTimerRef.current = setTimeout(() => {
      setBreathLabel(BREATH_LABELS.hold);
      breathTimerRef.current = setTimeout(() => {
        setBreathLabel(BREATH_LABELS.exhale);
        setBreathScale(1);
        breathTimerRef.current = setTimeout(() => {
          setBreathLabel('');
          breathTimerRef.current = setTimeout(() => {
            runBreathCycle(n + 1);
          }, BREATH_REST);
        }, BREATH_EXHALE);
      }, BREATH_HOLD);
    }, BREATH_INHALE);
  }, []);

  // Start on tap · iOS-safe: create + prime audio synchronously inside gesture
  const handleTap = useCallback(() => {
    if (mode !== 'waiting') return;
    audioRef.current = new Audio(AUDIO_URL);
    audioRef.current.volume = 0;
    audioRef.current.preload = 'auto';
    // iOS unlock — silent play + immediate pause inside gesture
    try {
      const p = audioRef.current.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) { /* noop */ }
        }).catch(() => { /* silent — will fall back at play time */ });
      }
    } catch (_) { /* noop */ }
    setMode('breathing');
    runBreathCycle(0);
  }, [mode, runBreathCycle]);

  // Immersion timer
  useEffect(() => {
    if (mode !== 'immersion') return;

    // The master mix already has music + binaural + subliminal baked
    // in at the correct levels, so we just fade the voice up to full.
    const VOICE_TARGET = 0.95;

    setTimeout(() => {
      if (audioRef.current && audioRef.current.src) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().then(() => {
          let vol = 0;
          const fade = setInterval(() => {
            vol += 0.015;
            if (vol >= VOICE_TARGET || !audioRef.current) {
              clearInterval(fade);
              if (audioRef.current) audioRef.current.volume = VOICE_TARGET;
            } else if (audioRef.current) {
              audioRef.current.volume = vol;
            }
          }, 60);
        }).catch(() => { /* autoplay blocked — visuals still play */ });
      }
    }, 1500);

    const start = Date.now();
    timerRef.current = setInterval(() => {
      const s = (Date.now() - start) / 1000;
      elapsedRef.current = s;
      setElapsed(s);
      if (s >= DISSOLVE_START) {
        setDissolveOpacity(Math.min(1, (s - DISSOLVE_START) / (TOTAL_DURATION - DISSOLVE_START)));
        if (audioRef.current && audioRef.current.volume > 0.005) {
          audioRef.current.volume = Math.max(0, audioRef.current.volume - 0.005);
        }
      }
      if (s >= TOTAL_DURATION) {
        clearInterval(timerRef.current);
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        setMode('done');
        setTimeout(onComplete, 2000);
      }
    }, 200);
    setShowSkip(true);

    // Subliminal cycling — Russian phrases
    subliminalTimerRef.current = setInterval(() => {
      const idx = subliminalIndexRef.current % SUBLIMINAL_RU.length;
      setSubliminalText(SUBLIMINAL_RU[idx]);
      subliminalIndexRef.current++;
      setTimeout(() => setSubliminalText(''), 3500);
    }, 5000);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(subliminalTimerRef.current);
    };
  }, [mode, onComplete]);

  // Text sync overlay
  useEffect(() => {
    if (mode !== 'immersion') return;
    const check = setInterval(() => {
      const s = elapsedRef.current;
      for (const entry of TEXT_SYNC) {
        if (s >= entry.t && s < entry.t + entry.dur / 1000 && !shownTextsRef.current.has(entry.t)) {
          shownTextsRef.current.add(entry.t);
          setOverlayText(entry.text);
          setTimeout(() => setOverlayText(null), entry.dur);
          break;
        }
      }
    }, 300);
    return () => clearInterval(check);
  }, [mode]);

  // Sync mute state
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  // Cleanup on unmount
  useEffect(() => () => {
    clearTimeout(breathTimerRef.current);
    clearInterval(timerRef.current);
    clearInterval(subliminalTimerRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  const handleSkip = () => {
    clearInterval(timerRef.current);
    clearInterval(subliminalTimerRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setMode('done');
    setTimeout(onComplete, 500);
  };

  return (
    <motion.div
      className="fixed inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2 }}
      data-testid="heart-immersion-ru"
    >
      {/* Warm dark base */}
      <div
        className="fixed inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 42%, rgba(20,14,8,1) 0%, rgba(8,5,3,1) 60%)' }}
      />

      {/* Cinematic heart visual — same engine as the English healing */}
      {mode === 'immersion' && (
        <motion.div
          className="fixed inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 3 }}
        >
          <HeartVisualEngine
            elapsed={elapsed}
            totalDuration={TOTAL_DURATION}
            isActive={true}
          />
        </motion.div>
      )}

      {/* Dissolve overlay */}
      {dissolveOpacity > 0 && (
        <motion.div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'rgba(10,7,4,1)' }}
          animate={{ opacity: dissolveOpacity }}
        />
      )}

      {/* Waiting — Начать */}
      <AnimatePresence>
        {mode === 'waiting' && (
          <motion.div
            className="fixed inset-0 flex flex-col items-center justify-center z-20 cursor-pointer"
            onClick={handleTap}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            data-testid="heart-ru-tap-begin"
          >
            <motion.h2
              className="text-2xl text-white/50 mb-8"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}
            >
              Сердце
            </motion.h2>
            <motion.p
              className="text-white/35 text-base"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}
              animate={{ opacity: [0.25, 0.5, 0.25] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              Нажми чтобы начать
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Breathing phase — no heart glow yet, just the settling ring */}
      <AnimatePresence>
        {mode === 'breathing' && (
          <motion.div
            className="fixed inset-0 flex flex-col items-center justify-center z-20 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            <p
              className="text-white/20 text-xs mb-6 tracking-widest uppercase"
              style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.3em' }}
            >
              Успокойся
            </p>
            <motion.div
              className="rounded-full"
              style={{
                width: 80, height: 80,
                border: '1px solid rgba(210,175,130,0.15)',
                background: 'radial-gradient(circle, rgba(210,175,130,0.06) 0%, transparent 60%)',
              }}
              animate={{ scale: breathScale }}
              transition={{ duration: breathScale > 1 ? BREATH_INHALE / 1000 : BREATH_EXHALE / 1000, ease: 'easeInOut' }}
            />
            {breathLabel && (
              <motion.p
                className="text-white/40 text-sm mt-6"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                key={breathLabel}
              >
                {breathLabel}
              </motion.p>
            )}
            <p
              className="text-white/15 text-xs mt-3"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {breathCount + 1} из {TOTAL_BREATHS}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Immersion overlay text */}
      {mode === 'immersion' && (
        <div className="fixed inset-0 flex flex-col items-center justify-end pb-32 z-10 pointer-events-none">
          <AnimatePresence mode="wait">
            {overlayText && (
              <motion.p
                key={overlayText}
                className="text-white/50 text-lg text-center px-10"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 1.5 }}
              >
                {overlayText}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Subliminal — very quiet text */}
          <AnimatePresence>
            {subliminalText && (
              <motion.p
                key={subliminalText}
                className="text-white text-xs mt-4 text-center"
                style={{ fontFamily: "'Manrope', sans-serif", opacity: 0.04 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.04 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2 }}
              >
                {subliminalText}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Controls */}
      {mode === 'immersion' && (
        <div className="fixed bottom-6 left-0 right-0 flex items-center justify-between px-6 z-20">
          <p className="text-white/15 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {formatMins(elapsed)}
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMuted(!muted)}
              className="p-2 text-white/20"
              data-testid="heart-ru-mute-btn"
              aria-label="mute"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            {showSkip && (
              <button
                onClick={handleSkip}
                className="p-2 text-white/20"
                data-testid="heart-ru-skip-btn"
                aria-label="skip"
              >
                <SkipForward size={16} />
              </button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default HeartImmersionRu;
